import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  CircularProgress,
  Tooltip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Menu,
  MenuItem,
  Drawer,
  useMediaQuery,
  useTheme,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { API_BASE_URL } from './config';
import MessageRenderer, { findExportableProductIds, handleProductIdsExport } from './MessageRenderer';

const EXPORT_REGEX = /\b(export|download|save)\b/i;
const EXPORT_IMAGES_REGEX = /\b(image|images|photo|photos|picture|pictures|zip)\b/i;

const PRIMARY = '#1d5576';
const ACCENT = '#f9a91e';
const SIDEBAR_WIDTH = 260;

const LANGUAGES = [
  { label: 'English',  code: 'en-US', voicePattern: /english/i },
  { label: 'Spanish',  code: 'es-ES', voicePattern: /spanish|español|es[-_]/i },
  { label: 'Chinese',  code: 'zh-CN', voicePattern: /chinese|mandarin|zh[-_]/i },
  { label: 'Filipino', code: 'fil-PH', voicePattern: /filipino|tagalog|fil/i },
];

const INITIAL_MESSAGES = [
  {
    role: 'assistant',
    content: "Hi! I'm your Gibson Sales Portal assistant. Ask me anything about products, catalogs, brands, or how to use the portal.",
  },
];

const SUGGESTIONS = [
  'Show me new items from the latest catalog',
  'What clearance catalogs are available?',
  'Show me bestselling products',
  'What brands do we carry?',
];

// ── voice helpers ───────────────────────────────────────────────────────────
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

function stripMarkdown(text) {
  return text
    .replace(/^-{2,}$/gm, '')           // remove --- horizontal rules
    .replace(/#{1,3} /g, '')             // remove headings
    .replace(/\*\*([^*]+)\*\*/g, '$1')  // remove bold
    .replace(/\*([^*]+)\*/g, '$1')      // remove italic
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // remove links, keep label
    .replace(/^[-*]\s+/gm, '')          // remove bullet points
    .replace(/^\d+\.\s+/gm, '')         // remove numbered lists
    .replace(/\n{2,}/g, '. ')           // double newlines become pause
    .replace(/\n/g, ' ')                // single newlines become space
    .replace(/\s{2,}/g, ' ')            // collapse extra spaces
    .trim();
}

function buildProductSpeech(reply, productCards) {
  if (!productCards || productCards.length === 0) return stripMarkdown(reply);

  const count = productCards.length;
  const intro = count === 1
    ? `I found 1 product for you.`
    : `I found ${count} products for you.`;

  const details = productCards.slice(0, 3).map((card) => {
    const parts = [`${card.product_name}`];
    if (card.item_number) parts.push(`item number ${card.item_number}`);
    if (card.material)    parts.push(`material ${card.material}`);
    if (card.color)       parts.push(`color ${card.color}`);
    if (card.category)    parts.push(`category ${card.category}`);
    return parts.join(', ');
  });

  const outro = count > 3 ? `And ${count - 3} more. Check the screen for full details.` : `Check the screen for full details.`;
  return [intro, ...details, outro].join('. ');
}

function speakText(text, productCards, onEnd, voiceName) {
  window.speechSynthesis.cancel();
  const speech = buildProductSpeech(text, productCards);
  const utterance = new SpeechSynthesisUtterance(speech);
  utterance.rate = 0.95;
  utterance.pitch = 1;
  if (voiceName) {
    const voice = window.speechSynthesis.getVoices().find(v => v.name === voiceName);
    if (voice) utterance.voice = voice;
  }
  if (onEnd) utterance.onend = onEnd;
  window.speechSynthesis.speak(utterance);
}

// ── helpers ────────────────────────────────────────────────────────────────
function formatSessionDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString();
}

// ── Main Component ──────────────────────────────────────────────────────────
const AIAssistant = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // sidebar visibility
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // session list
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // active session
  const [activeSessionId, setActiveSessionId] = useState(null);

  // messages in active session
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [modelName, setModelName] = useState('');

  // context menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuSessionId, setMenuSessionId] = useState(null);

  const [listening, setListening] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [selectedLang, setSelectedLang] = useState(LANGUAGES[0]);
  const selectedLangRef = useRef(LANGUAGES[0]);
  const [portalSettings, setPortalSettings] = useState({ allow_language_change: false, allow_voice: false });
  const selectedVoiceRef = useRef('');
  const messagesRef = useRef(messages);
  const recognitionRef = useRef(null);
  const abortControllerRef = useRef(null);
  const voiceModeRef = useRef(false);

  // Load available voices — browser fires voiceschanged when ready
  useEffect(() => {
    const load = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return;
      setAvailableVoices(voices);
      pickVoiceForLang(voices, LANGUAGES[0]);
    };
    load();
    window.speechSynthesis.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
  }, []);

  const pickVoiceForLang = (voices, lang) => {
    const pool = voices.length ? voices : window.speechSynthesis.getVoices();
    let preferred;
    if (lang.label === 'English') {
      preferred = pool.find(v => v.name === 'Google US English')
        || pool.find(v => /google/i.test(v.name) && /en/i.test(v.lang))
        || pool.find(v => /en[-_]US/i.test(v.lang));
    } else {
      preferred = pool.find(v => lang.voicePattern.test(v.name) || v.lang.startsWith(lang.code.slice(0, 2)));
    }
    const name = preferred?.name || '';
    setSelectedVoice(name);
    selectedVoiceRef.current = name;
  };

  const handleLangChange = (lang) => {
    setSelectedLang(lang);
    selectedLangRef.current = lang;
    pickVoiceForLang(availableVoices, lang);
    // Restart recognition in new language if voice mode is on
    if (voiceModeRef.current) {
      recognitionRef.current?.stop();
      setTimeout(() => startListening(), 300);
    }
  };

  const bottomRef = useRef(null);
  const token = localStorage.getItem('access');

  // ── keep messages ref current ─────────────────────────────────────────────
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // ── scroll to bottom ──────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // ── fetch session list ────────────────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/sessions/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (err) {
      // silently ignore
    } finally {
      setSessionsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSessions();
    fetch(`${API_BASE_URL}/api/portal-settings/`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null).then(data => { if (data) setPortalSettings(data); });
  }, [fetchSessions]);

  // ── load a session ────────────────────────────────────────────────────────
  const loadSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    setInput('');
    if (isMobile) setSidebarOpen(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const loaded = data.messages.map((m) => ({
          role: m.role,
          content: m.content,
          table_data: m.table_data || null,
          product_cards: m.product_cards || null,
        }));
        setMessages(loaded.length > 0 ? loaded : INITIAL_MESSAGES);
      }
    } catch (err) {
      // ignore
    }
  };

  // ── new chat ──────────────────────────────────────────────────────────────
  const startNewChat = () => {
    setActiveSessionId(null);
    setMessages(INITIAL_MESSAGES);
    setInput('');
    if (isMobile) setSidebarOpen(false);
  };

  // ── delete session ────────────────────────────────────────────────────────
  const deleteSession = async (sessionId) => {
    try {
      await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSessionId === sessionId) startNewChat();
    } catch (err) {
      // ignore
    }
  };

  // ── toggle save ───────────────────────────────────────────────────────────
  const toggleSave = async (sessionId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}/save/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSessions((prev) =>
          prev.map((s) => (s.id === sessionId ? { ...s, is_saved: data.is_saved } : s))
        );
      }
    } catch (err) {
      // ignore
    }
  };

  // ── send message ──────────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = selectedLangRef.current.code;
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
      setAudioEnabled(true);
      setTimeout(() => sendMessage(transcript), 1500);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => {
      setListening(false);
      if (voiceModeRef.current && !window.speechSynthesis.speaking) {
        setTimeout(() => startListening(), 300);
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopProcessing = () => {
    abortControllerRef.current?.abort();
    setLoading(false);
    if (voiceModeRef.current) startListening();
  };

  const sendMessage = async (voiceText) => {
    const text = (voiceText || input).trim();
    if (!text || loading) return;

    const currentMessages = messagesRef.current;

    // Intercept export intent: only act when real, exportable products were actually
    // shown earlier in the conversation — otherwise fall through to the normal chat
    // flow, which correctly explains there's nothing to export yet.
    if (EXPORT_REGEX.test(text)) {
      const productIds = findExportableProductIds(currentMessages);
      if (productIds) {
        const wantsImages = EXPORT_IMAGES_REGEX.test(text);
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: text },
          {
            role: 'assistant',
            content: wantsImages
              ? 'Downloading the images as a ZIP file — your download should start shortly.'
              : 'Exporting to Excel — your download should start shortly.',
          },
        ]);
        setInput('');
        if (wantsImages) {
          handleProductIdsExport('/api/download-product-images/', productIds, `product_images_${productIds.length}.zip`);
        } else {
          handleProductIdsExport('/api/export-products/', productIds, `products_${productIds.length}.xlsx`);
        }
        return;
      }
    }

    const userMsg = { role: 'user', content: text };
    const updatedMessages = [...currentMessages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    const history = updatedMessages.slice(1, -1);

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const res = await fetch(`${API_BASE_URL}/api/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, history, session_id: activeSessionId, language: selectedLangRef.current.label, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
        signal: controller.signal,
      });
      const data = await res.json();

      if (res.status === 401) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Your session has expired. Redirecting you to the login page…' },
        ]);
        setTimeout(() => {
          localStorage.clear();
          sessionStorage.clear();
          navigate('/');
        }, 1500);
        return;
      }
      if (!res.ok) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.error || data.detail || 'Something went wrong. Please try again.' },
        ]);
        return;
      }

      if (data.model && !modelName) setModelName(data.model);

      // If backend assigned/created a session, track it
      if (data.session_id && !activeSessionId) {
        setActiveSessionId(data.session_id);
        // Refresh session list so the new one appears
        fetchSessions();
      } else if (data.session_id) {
        // Update title if it changed (first message sets the title)
        fetchSessions();
      }

      const reply = data.reply || data.error || 'No response received.';
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: reply,
          table_data: data.table_data || null,
          product_cards: data.product_cards || null,
          suggestions: data.suggestions || [],
        },
      ]);
      if (audioEnabled) {
        speakText(reply, data.product_cards, () => {
          if (voiceModeRef.current) startListening();
        }, selectedVoiceRef.current);
      } else if (voiceModeRef.current) {
        startListening();
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleListening = () => {
    if (!SpeechRecognition) return;
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
    } else {
      startListening();
    }
  };

  const toggleVoiceMode = () => {
    if (!SpeechRecognition) return;
    const next = !voiceMode;
    voiceModeRef.current = next;
    setVoiceMode(next);
    if (next) {
      setAudioEnabled(true);
      startListening();
    } else {
      setAudioEnabled(false);
      recognitionRef.current?.stop();
      window.speechSynthesis.cancel();
      setListening(false);
    }
  };

  // ── context menu ──────────────────────────────────────────────────────────
  const handleMenuOpen = (e, sessionId) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuSessionId(sessionId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuSessionId(null);
  };

  // ── split sessions ────────────────────────────────────────────────────────
  const savedSessions = sessions.filter((s) => s.is_saved);
  const recentSessions = sessions.filter((s) => !s.is_saved);

  // ── sidebar content ───────────────────────────────────────────────────────
  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8f9fb',
        borderRight: '1px solid #e0e0e0',
      }}
    >
      {/* Sidebar header */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY }}>
          Chat History
        </Typography>
        <Tooltip title="New chat">
          <IconButton size="small" onClick={startNewChat} sx={{ color: PRIMARY }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {sessionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress size={22} sx={{ color: PRIMARY }} />
          </Box>
        ) : (
          <>
            {/* Saved sessions */}
            {savedSessions.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}
                >
                  Saved
                </Typography>
                <List dense disablePadding>
                  {savedSessions.map((s) => (
                    <SessionItem
                      key={s.id}
                      session={s}
                      active={activeSessionId === s.id}
                      onSelect={() => loadSession(s.id)}
                      onMenuOpen={handleMenuOpen}
                    />
                  ))}
                </List>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}

            {/* Recent sessions */}
            {recentSessions.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}
                >
                  Recent
                </Typography>
                <List dense disablePadding>
                  {recentSessions.map((s) => (
                    <SessionItem
                      key={s.id}
                      session={s}
                      active={activeSessionId === s.id}
                      onSelect={() => loadSession(s.id)}
                      onMenuOpen={handleMenuOpen}
                    />
                  ))}
                </List>
              </>
            )}

            {sessions.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 2, display: 'block' }}>
                No chat history yet.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 90px)', maxWidth: '1100px', mx: 'auto' }}>
      {/* Sidebar — drawer on mobile, inline on desktop */}
      {isMobile ? (
        <Drawer
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          sx={{
            width: sidebarOpen ? SIDEBAR_WIDTH : 0,
            minWidth: sidebarOpen ? SIDEBAR_WIDTH : 0,
            overflow: 'hidden',
            transition: 'width 0.2s, min-width 0.2s',
            flexShrink: 0,
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Chat panel */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1, sm: 2, md: 3 },
          pt: 2,
          pb: 1,
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title={sidebarOpen ? 'Hide history' : 'Show history'}>
              <IconButton size="small" onClick={() => setSidebarOpen((p) => !p)} sx={{ color: 'text.secondary' }}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Avatar sx={{ bgcolor: PRIMARY, width: 40, height: 40 }}>
              <SmartToyIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, lineHeight: 1.2 }}>
                Gibson AI
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Powered by Oracle GenAI
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {portalSettings.allow_language_change && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedLang.code}
                  onChange={(e) => handleLangChange(LANGUAGES.find(l => l.code === e.target.value))}
                  sx={{ fontSize: '0.78rem', borderRadius: '8px', height: 32 }}
                >
                  {LANGUAGES.map(lang => (
                    <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: '0.78rem' }}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Tooltip title="New chat">
              <IconButton onClick={startNewChat} size="small" sx={{ color: 'text.secondary' }}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Message list */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pr: 0.5,
          }}
        >
          {(() => {
            const lastAsstIdx = messages.reduce((last, m, i) => m.role === 'assistant' ? i : last, -1);
            return messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: msg.role === 'user' ? ACCENT : PRIMARY,
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                  }}
                >
                  {msg.role === 'user' ? (
                    <PersonIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <SmartToyIcon sx={{ fontSize: 18 }} />
                  )}
                </Avatar>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: '80%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: msg.role === 'user' ? `${PRIMARY}10` : '#f5f5f5',
                      border: `1px solid ${msg.role === 'user' ? `${PRIMARY}30` : '#e0e0e0'}`,
                      borderRadius:
                        msg.role === 'user'
                          ? '16px 4px 16px 16px'
                          : '4px 16px 16px 16px',
                    }}
                  >
                    <MessageRenderer
                      content={msg.content}
                      compact={false}
                      tableData={msg.table_data}
                      productCards={msg.product_cards}
                    />
                  </Paper>

                  {/* Contextual follow-up chips — only on the last AI message, only when idle */}
                  {msg.role === 'assistant' && idx === lastAsstIdx && !loading && msg.suggestions?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {msg.suggestions.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          onClick={() => sendMessage(s)}
                          variant="outlined"
                          size="small"
                          sx={{
                            cursor: 'pointer',
                            borderColor: `${PRIMARY}50`,
                            color: PRIMARY,
                            fontSize: '0.72rem',
                            height: 26,
                            '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ));
          })()}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: PRIMARY, width: 34, height: 34 }}>
                <SmartToyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px 16px 16px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={14} thickness={5} sx={{ color: PRIMARY }} />
                <Typography variant="body2" color="text.secondary">
                  Thinking...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={bottomRef} />
        </Box>

        {/* Suggestion chips — shown only on a fresh empty session */}
        {messages.length === 1 && !loading && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', py: 1.5 }}>
            {SUGGESTIONS.map((s) => (
              <Chip
                key={s}
                label={s}
                onClick={() => sendMessage(s)}
                variant="outlined"
                size="small"
                sx={{
                  cursor: 'pointer',
                  borderColor: `${PRIMARY}50`,
                  color: PRIMARY,
                  fontSize: '0.75rem',
                  '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
                }}
              />
            ))}
          </Box>
        )}

        {/* Input area */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>

          {/* Always-on voice mode toggle — only shown if admin allows voice AND browser supports it */}
          {portalSettings.allow_voice && Boolean(SpeechRecognition) && (
            <Tooltip title={voiceMode ? 'Turn off always-on voice' : 'Turn on always-on voice'}>
              <IconButton
                onClick={toggleVoiceMode}
                sx={{
                  width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                  bgcolor: voiceMode ? '#2e7d32' : '#f0f0f0',
                  color: voiceMode ? 'white' : '#aaa',
                  '&:hover': { bgcolor: voiceMode ? '#1b5e20' : '#e0e0e0' },
                }}
              >
                <RecordVoiceOverIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={listening ? 'Listening...' : voiceMode ? 'Always-on voice active — speak anytime...' : 'Ask a question about the portal...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused fieldset': { borderColor: PRIMARY },
                ...(listening && { '& fieldset': { borderColor: '#e53935', borderWidth: 2 } }),
              },
            }}
          />

          {/* Single mic tap — only shown if admin allows voice AND browser supports it */}
          {portalSettings.allow_voice && Boolean(SpeechRecognition) && (
            <Tooltip title={listening ? 'Stop listening' : 'Tap to speak'}>
              <IconButton
                onClick={toggleListening}
                sx={{
                  width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                  bgcolor: listening ? '#e53935' : `${PRIMARY}15`,
                  color: listening ? 'white' : PRIMARY,
                  '&:hover': { bgcolor: listening ? '#c62828' : `${PRIMARY}25` },
                }}
              >
                {listening ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}

          {/* Send / Stop */}
          {loading ? (
            <Tooltip title="Stop processing">
              <IconButton
                onClick={stopProcessing}
                sx={{
                  bgcolor: '#e53935', color: 'white',
                  width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                  '&:hover': { bgcolor: '#c62828' },
                }}
              >
                <StopCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim()}
              sx={{
                bgcolor: PRIMARY, color: 'white',
                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                '&:hover': { bgcolor: '#164060' },
                '&.Mui-disabled': { bgcolor: '#ccc', color: 'white' },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
          Press Enter to send · Tap mic to speak · Green mic = always-on voice
        </Typography>
      </Box>

      {/* Context menu for session actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 2, sx: { minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            toggleSave(menuSessionId);
            handleMenuClose();
          }}
        >
          {sessions.find((s) => s.id === menuSessionId)?.is_saved ? (
            <><BookmarkIcon fontSize="small" sx={{ mr: 1, color: PRIMARY }} /> Unsave</>
          ) : (
            <><BookmarkBorderIcon fontSize="small" sx={{ mr: 1 }} /> Save conversation</>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            deleteSession(menuSessionId);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

// ── SessionItem sub-component ───────────────────────────────────────────────
const SessionItem = ({ session, active, onSelect, onMenuOpen }) => {
  const PRIMARY = '#1d5576';
  return (
    <ListItemButton
      selected={active}
      onClick={onSelect}
      sx={{
        py: 0.75,
        px: 1.5,
        '&.Mui-selected': {
          bgcolor: `${PRIMARY}15`,
          '&:hover': { bgcolor: `${PRIMARY}20` },
        },
        '&:hover .session-menu-btn': { opacity: 1 },
        borderRadius: '6px',
        mx: 0.5,
        mb: 0.25,
      }}
    >
      <ListItemText
        primary={
          <Typography
            variant="body2"
            noWrap
            sx={{ fontSize: '0.82rem', fontWeight: active ? 600 : 400, color: active ? PRIMARY : 'text.primary' }}
          >
            {session.title || 'Untitled'}
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            {formatSessionDate(session.updated_at)}
          </Typography>
        }
      />
      <ListItemSecondaryAction sx={{ right: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {session.is_saved && (
            <BookmarkIcon sx={{ fontSize: 14, color: PRIMARY, opacity: 0.7 }} />
          )}
          <IconButton
            className="session-menu-btn"
            size="small"
            onClick={(e) => onMenuOpen(e, session.id)}
            sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25 }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </ListItemSecondaryAction>
    </ListItemButton>
  );
};

export default AIAssistant;
